import Parser, {
  type IArcEntity,
  type ICircleEntity,
  type IDimensionEntity,
  type IDxf,
  type IEntity as IDxfEntity,
  type ILayer as IDxfLayer,
  type IEllipseEntity,
  type ILwpolylineEntity,
  type IMtextEntity,
  type IPolylineEntity,
  type ITextEntity,
} from 'dxf-parser'
import { nanoid } from 'nanoid'
import {
  ColorMethod,
  EntityType,
  LineType,
} from '@/plugins/cad/module/CADModule/model/common/constant'
import {
  type AlignDimensionEntity,
  type AngularDimensionEntity,
  EntityFactory,
  type IEntity,
  type MLeaderEntity,
  type MTextEntity,
  type PolylineEntity,
  type RayEntity,
  type SplineEntity,
} from '@/plugins/cad/module/CADModule/model/entity'
import { Layer } from '@/plugins/cad/module/CADModule/model/layer/Layer'
import { getLoggerManager } from '@/shared/logging'
import MLeaderEntityParser, {
  type IDxfMLeaderEntity,
  MultiLeaderEntityParser,
} from './entities/mLeader'
import RayEntityParser, { type IDxfRayEntity } from './entities/ray'
import SplineEntityParser, { type IDxfSplineEntity } from './entities/spline'

/**
 * 将 DXF 文本中的 Unicode 转义还原为实际字符
 * 例: 2\U+2032-6 3/8\U+2033 → 2′-6 3/8″
 */
function decodeUnicodeEscape(match: string, hex: string) {
  const codePoint = Number.parseInt(hex, 16)
  if (!Number.isInteger(codePoint) || codePoint > 0x10ffff) return match
  return String.fromCodePoint(codePoint)
}

/**
 * 去除MTEXT格式控制码，提取纯文本内容
 * 例: {\fSimSun|b0|i0|c134|p2;啊啊} → 啊啊
 */
function stripMTextFormatting(text: string) {
  return text
    .replace(/\{\\f[^;]*;([^}]*)}/g, '$1')
    .replace(/\{\\[A-Za-z][^;]*;([^}]*)}/g, '$1')
    .replace(/\\[LOKlok]/g, '')
    .replace(/\\[Cc]\d+;/g, '')
    .replace(/\\[Hh][\d.]+x?;/g, '')
    .replace(/\\[Ww][\d.]+;/g, '')
    .replace(/\\[Qq][\d.]+;/g, '')
    .replace(/\\[Tt][\d.]+;/g, '')
    .replace(/\\[Aa]\d;/g, '')
    .replace(/\\P/gi, '\n')
    .replace(/[{}]/g, '')
    .replace(/\\\\/g, '\\')
    .replace(/\\U\+([0-9A-Fa-f]{4,6})/g, decodeUnicodeEscape)
    .trim()
}

// dxf解析的EntityName到EntityType的映射
const EntityTypeMapping: Partial<Record<string, EntityType>> = {
  LINE: EntityType.Polyline,
  POLYLINE: EntityType.Polyline,
  LWPOLYLINE: EntityType.Polyline,
  ARC: EntityType.Arc,
  CIRCLE: EntityType.Circle,
  SPLINE: EntityType.Spline,
  ELLIPSE: EntityType.Ellipse,
  RAY: EntityType.Ray,
  TEXT: EntityType.MText,
  MTEXT: EntityType.MText,
  MLEADER: EntityType.MLeader,
  MULTILEADER: EntityType.MLeader,
}

/** DXF 线型名称到 LineType 枚举的映射 */
const DXF_LINE_TYPE_MAPPING: Record<string, LineType> = {
  CONTINUOUS: LineType.Solid,
  DASHED: LineType.Dash,
  DASHDOT: LineType.DashDot,
  DOUBLEDASH: LineType.DoubleDash,
}

/** 解析 DXF 线型名称 */
function parseLineType(lineType?: string) {
  if (!lineType) {
    return undefined
  }
  const normalized = lineType.toUpperCase()
  if (normalized === 'BYLAYER' || normalized === 'BYBLOCK') {
    return undefined
  }
  return DXF_LINE_TYPE_MAPPING[normalized] ?? LineType.Solid
}

/** 解析 DXF 线宽（百分之一毫米 → 毫米） */
function parseLineWidth(lineweight?: number) {
  if (lineweight === undefined || lineweight < 0) {
    return undefined
  }
  return lineweight / 100
}

/** 归一化方向向量 */
function normalizeVector(point?: { x?: number; y?: number }) {
  if (!point) {
    return null
  }
  const length = Math.hypot(point.x || 0, point.y || 0)
  if (length < 1e-12) {
    return null
  }
  return {
    x: (point.x || 0) / length,
    y: (point.y || 0) / length,
  }
}

/** 获取标注基本类型（低 4 位） */
function getDimensionBaseType(dimensionType: number = 0) {
  return dimensionType & 0x0f
}

/** 解析时需要跳过的图层名集合，例如 AutoCAD 的 Defpoints 辅助图层 */
export const IgnoreLayerSet: Set<string> = new Set(['Defpoints'])

const log = getLoggerManager().getLogger('cad')

/** DXF 源文本解析器，封装 dxf-parser 并归一化为内部 CAD 元数据模型 */
export class DxfParser {
  /**
   * 解析 DXF 源文本为 CAD 元数据。
   *
   * @param data - DXF 文件原始文本
   * @returns 解析后的图层与实体列表，解析失败返回 null
   */
  static parse(data: string): { layers: Layer[]; entities: IEntity[] } | null {
    const parser = new Parser()
    parser.registerEntityHandler(SplineEntityParser as any)
    parser.registerEntityHandler(RayEntityParser as any)
    parser.registerEntityHandler(MLeaderEntityParser as any)
    parser.registerEntityHandler(MultiLeaderEntityParser as any)
    const dxf = parser.parse(data)
    return dxf ? DxfParser.parseDxf(dxf) : null
  }

  private static parseDxf(dxf: IDxf): { layers: Layer[]; entities: IEntity[] } {
    const result: { layers: Layer[]; entities: IEntity[] } = {
      layers: [],
      entities: [],
    }
    const dxfLayers = dxf.tables.layer.layers
    const layerMap: Map<string, Layer> = new Map()

    for (const layerName in dxfLayers) {
      const layer = DxfParser.parseLayer(dxfLayers[layerName])
      layerMap.set(layer.name, layer)
    }

    const defaultLayer = layerMap.entries().next().value?.[1]
    for (const dxfEntity of dxf.entities || []) {
      let entity: IEntity | null = null
      try {
        entity = DxfParser.parseEntity(dxfEntity, dxf)
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        log.warn(`DXF entity parse skipped, type=${dxfEntity.type}, reason=${message}`)
      }
      if (!entity) {
        continue
      }

      const layer = layerMap.get(dxfEntity.layer) || defaultLayer
      Object.assign(entity, DxfParser.getEntityBaseData(dxfEntity, layer))
      result.entities.push(entity)
    }

    result.layers = Array.from(layerMap.values())
    return result
  }

  private static parseLayer(dxfLayer: IDxfLayer) {
    return new Layer({
      id: nanoid(),
      name: dxfLayer.name,
      color: dxfLayer.color,
      visible: dxfLayer.visible,
    })
  }

  private static parseEntity(dxfEntity: IDxfEntity, dxf: IDxf): IEntity | null {
    let entityType = EntityTypeMapping[dxfEntity.type]
    if (dxfEntity.type === 'DIMENSION') {
      entityType = DxfParser.getDimensionEntityType(dxfEntity as IDimensionEntity)
    }

    let entityData: any
    switch (entityType) {
      case EntityType.Polyline: {
        const { vertices, shape = false } = dxfEntity as IPolylineEntity | ILwpolylineEntity
        const data: Partial<PolylineEntity> = {
          vertices:
            vertices?.map((vertex) => ({
              x: vertex.x,
              y: vertex.y,
              bulge: vertex.bulge || 0,
            })) || [],
          isClosed: !!shape,
        }
        if (data.vertices && data.vertices.length >= 2) {
          entityData = data
        }
        break
      }
      case EntityType.Arc: {
        const { center, radius, startAngle, endAngle } = dxfEntity as IArcEntity
        entityData = {
          center: { x: center.x, y: center.y },
          radius,
          startAngle,
          endAngle,
          clockwise: false,
        }
        break
      }
      case EntityType.Circle: {
        const { center, radius } = dxfEntity as ICircleEntity
        entityData = {
          center: { x: center.x, y: center.y },
          radius,
        }
        break
      }
      case EntityType.Spline: {
        const splineEntity = dxfEntity as IDxfSplineEntity
        const data: Partial<SplineEntity> = {
          controlPoints:
            splineEntity.controlPoints?.map((point) => ({ x: point.x, y: point.y })) || [],
          fitPoints: splineEntity.fitPoints?.map((point) => ({ x: point.x, y: point.y })),
          knots: splineEntity.knotValues,
          weights: splineEntity.weights,
          isClosed: splineEntity.closed,
          degree: splineEntity.degreeOfSplineCurve,
          method: splineEntity.fitPoints?.length ? 'FIT' : 'CV',
          startTangent: splineEntity.startTangent
            ? { x: splineEntity.startTangent.x, y: splineEntity.startTangent.y }
            : undefined,
          endTangent: splineEntity.endTangent
            ? { x: splineEntity.endTangent.x, y: splineEntity.endTangent.y }
            : undefined,
        }
        if (data.controlPoints && data.controlPoints.length >= 2) {
          entityData = data
        }
        break
      }
      case EntityType.Ellipse: {
        const { center, majorAxisEndPoint, axisRatio, startAngle, endAngle } =
          dxfEntity as IEllipseEntity
        entityData = {
          center: { x: center.x, y: center.y },
          majorAxisEndPoint: { x: majorAxisEndPoint.x, y: majorAxisEndPoint.y },
          axisRatio,
          startAngle,
          endAngle,
          clockwise: false,
        }
        break
      }
      case EntityType.Ray: {
        const { startPoint, unitVector } = dxfEntity as IDxfRayEntity
        const direction = normalizeVector(unitVector)
        if (!startPoint || !direction) {
          break
        }

        entityData = {
          startPoint: { x: startPoint.x, y: startPoint.y },
          unitVector: direction,
        } satisfies Partial<RayEntity>
        break
      }
      case EntityType.MText: {
        if (dxfEntity.type === 'TEXT') {
          const { startPoint, text, textHeight, rotation } = dxfEntity as ITextEntity
          const contents = stripMTextFormatting(text)
          if (contents) {
            entityData = {
              position: { x: startPoint.x, y: startPoint.y },
              contents,
              textHeight,
              rotation: rotation ? (rotation * Math.PI) / 180 : 0,
            } satisfies Partial<MTextEntity>
          }
        } else {
          const { position, text, height, width, rotation, attachmentPoint } =
            dxfEntity as IMtextEntity
          const contents = stripMTextFormatting(text)
          if (contents) {
            entityData = {
              position: { x: position.x, y: position.y },
              contents,
              textHeight: height,
              rectWidth: width,
              rectHeight: height,
              rotation: rotation ? (rotation * Math.PI) / 180 : 0,
              attachment: attachmentPoint || 1,
            } satisfies Partial<MTextEntity>
          }
        }
        break
      }
      case EntityType.AlignDimension: {
        const { linearOrAngularPoint1, linearOrAngularPoint2, anchorPoint, middleOfText, text } =
          dxfEntity as IDimensionEntity
        if (!linearOrAngularPoint1 || !linearOrAngularPoint2 || !anchorPoint || !middleOfText) {
          break
        }
        const dimTextHeight = DxfParser.getDimTextHeight(
          (dxfEntity as IDimensionEntity).block,
          dxf.blocks
        )
        entityData = {
          p1: { x: linearOrAngularPoint1.x, y: linearOrAngularPoint1.y },
          p2: { x: linearOrAngularPoint2.x, y: linearOrAngularPoint2.y },
          offsetPoint: { x: anchorPoint.x, y: anchorPoint.y },
          textPosition: { x: middleOfText.x, y: middleOfText.y },
          dimensionText: text ? stripMTextFormatting(text) : '',
          isTextPositionEdited: !!middleOfText,
          ...(dimTextHeight !== undefined && { textHeight: dimTextHeight }),
        } satisfies Partial<AlignDimensionEntity>
        break
      }
      case EntityType.AngularDimension:
        entityData = DxfParser.parseAngularDimension(dxfEntity as IDimensionEntity, dxf)
        break
      case EntityType.MLeader:
        entityData = DxfParser.parseMLeader(dxfEntity as IDxfMLeaderEntity)
        break
      default:
        log.warn(`Unsupported DXF entity type: ${dxfEntity.type}`)
    }

    if (!entityData || !entityType) {
      return null
    }

    return EntityFactory.createEntity({
      ...entityData,
      id: nanoid(),
      type: entityType,
    } as any)
  }

  /** 解析角度标注 */
  private static parseAngularDimension(dxfEntity: IDimensionEntity, dxf: IDxf) {
    const baseType = getDimensionBaseType(dxfEntity.dimensionType)
    const dimTextHeight = DxfParser.getDimTextHeight(dxfEntity.block, dxf.blocks)
    const dimensionText = dxfEntity.text ? stripMTextFormatting(dxfEntity.text) : ''

    if (baseType === 5) {
      const intersection = dxfEntity.diameterOrRadiusPoint
      const p1 = dxfEntity.linearOrAngularPoint1
      const p2 = dxfEntity.linearOrAngularPoint2
      const arcPoint = dxfEntity.anchorPoint
      if (!intersection || !p1 || !p2 || !arcPoint) {
        return undefined
      }

      return {
        line1: {
          startPoint: { x: intersection.x, y: intersection.y },
          endPoint: { x: p1.x, y: p1.y },
        },
        line2: {
          startPoint: { x: intersection.x, y: intersection.y },
          endPoint: { x: p2.x, y: p2.y },
        },
        arcPoint: { x: arcPoint.x, y: arcPoint.y },
        textPosition: dxfEntity.middleOfText
          ? { x: dxfEntity.middleOfText.x, y: dxfEntity.middleOfText.y }
          : { x: arcPoint.x, y: arcPoint.y },
        dimensionText,
        isTextPositionEdited: !!dxfEntity.middleOfText,
        ...(dimTextHeight !== undefined && { textHeight: dimTextHeight }),
      } satisfies Partial<AngularDimensionEntity>
    }

    const line1Start = dxfEntity.linearOrAngularPoint1
    const line1End = dxfEntity.linearOrAngularPoint2
    const line2Start = dxfEntity.anchorPoint
    const line2End = dxfEntity.diameterOrRadiusPoint
    const arcPoint = dxfEntity.arcPoint || dxfEntity.anchorPoint
    if (!line1Start || !line1End || !line2Start || !line2End || !arcPoint) {
      return undefined
    }

    return {
      line1: {
        startPoint: { x: line1Start.x, y: line1Start.y },
        endPoint: { x: line1End.x, y: line1End.y },
      },
      line2: {
        startPoint: { x: line2Start.x, y: line2Start.y },
        endPoint: { x: line2End.x, y: line2End.y },
      },
      arcPoint: { x: arcPoint.x, y: arcPoint.y },
      textPosition: dxfEntity.middleOfText
        ? { x: dxfEntity.middleOfText.x, y: dxfEntity.middleOfText.y }
        : { x: arcPoint.x, y: arcPoint.y },
      dimensionText,
      isTextPositionEdited: !!dxfEntity.middleOfText,
      ...(dimTextHeight !== undefined && { textHeight: dimTextHeight }),
    } satisfies Partial<AngularDimensionEntity>
  }

  /** 解析多重引线 */
  private static parseMLeader(dxfEntity: IDxfMLeaderEntity) {
    const contents = stripMTextFormatting(dxfEntity.contents || '')
    const branches = (dxfEntity.branches || [])
      .filter((branch) => branch.arrowPoint && (branch.vertices?.length || branch.elbowPoint))
      .map((branch) => {
        const vertices = branch.vertices?.length
          ? branch.vertices
          : [branch.elbowPoint!, ...(branch.landingEndPoint ? [branch.landingEndPoint] : [])]

        return {
          arrowPoint: { x: branch.arrowPoint!.x, y: branch.arrowPoint!.y },
          vertices: vertices.map((vertex) => ({ x: vertex.x, y: vertex.y })),
        }
      })

    if (branches.length === 0 || !contents) {
      return undefined
    }

    const primaryBranch = branches[0]
    const fallbackTextPosition =
      primaryBranch.vertices[primaryBranch.vertices.length - 1] || primaryBranch.arrowPoint

    return {
      branches,
      textPosition: dxfEntity.textPosition
        ? { x: dxfEntity.textPosition.x, y: dxfEntity.textPosition.y }
        : { x: fallbackTextPosition.x, y: fallbackTextPosition.y },
      contents,
      textHeight: dxfEntity.textHeight ?? 2.5,
      landingGap: dxfEntity.landingGap ?? 2.5,
      hasLanding: dxfEntity.hasLanding ?? true,
      isTextPositionEdited: !!dxfEntity.textPosition,
    } satisfies Partial<MLeaderEntity>
  }

  /** 根据 dimensionType 判断标注子类型 */
  private static getDimensionEntityType(dxfEntity: IDimensionEntity) {
    const baseType = getDimensionBaseType(dxfEntity.dimensionType)
    if (baseType === 2 || baseType === 5) {
      return EntityType.AngularDimension
    }
    return EntityType.AlignDimension
  }

  /** 获取实体基础数据（颜色方法、线型、线宽等） */
  private static getEntityBaseData(dxfEntity: IDxfEntity, layer?: Layer) {
    const lineType = parseLineType(dxfEntity.lineType) ?? layer?.lineType ?? LineType.Solid
    const lineWidth = parseLineWidth(dxfEntity.lineweight) ?? layer?.lineWidth ?? 0.25

    let colorMethod = ColorMethod.BY_LAYER
    let color = layer?.color ?? dxfEntity.color ?? 0xffffff

    if (dxfEntity.colorIndex === 0) {
      colorMethod = ColorMethod.BY_BLOCK
      color = dxfEntity.color ?? color
    } else if (dxfEntity.colorIndex !== 256 && dxfEntity.color !== undefined) {
      colorMethod = ColorMethod.RGB
      color = dxfEntity.color
    }

    return {
      layerId: layer?.id || '',
      colorMethod,
      color,
      lineType,
      lineWidth,
    }
  }

  /** 从DIMENSION关联的block中查找MTEXT实体的文字高度 */
  private static getDimTextHeight(blockName: string, blocks: IDxf['blocks']) {
    if (!blockName) {
      return undefined
    }
    const block = blocks[blockName]
    if (!block) {
      return undefined
    }
    const mtext = block.entities.find((entity) => entity.type === 'MTEXT') as
      | IMtextEntity
      | undefined
    return mtext?.height
  }
}
